import type { CartLine, Personalisation, Product, ProductVariant } from "./types";

export class ValidationError extends Error {
  status: number;

  constructor(message: string, status = 400) {
    super(message);
    this.name = "ValidationError";
    this.status = status;
  }
}

export function formatAud(cents: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2
  }).format(cents / 100);
}

export function findVariant(product: Product, selections: Record<string, string>): ProductVariant | undefined {
  return product.variants.find((variant) =>
    product.options.every((option) => variant.selections[option.id] === selections[option.id])
  );
}

export function validatePersonalisation(product: Product, personalisation: Personalisation): void {
  const validStyles = product.styleSelector === "design"
    ? Array.from({ length: 8 }, (_, index) => String(index + 1))
    : Array.from({ length: 7 }, (_, index) => String(index + 1));

  if (!validStyles.includes(personalisation.style)) {
    throw new ValidationError(`Choose a valid ${product.styleSelector} style.`);
  }

  if (product.personalisationMode === "pair") {
    if (!personalisation.wordingOne?.trim() || !personalisation.wordingTwo?.trim()) {
      throw new ValidationError("Enter the wording for both signs.");
    }
    if (personalisation.wordingOne.length > 256 || personalisation.wordingTwo.length > 256) {
      throw new ValidationError("Sign wording must be 256 characters or fewer.");
    }
  } else {
    if (!personalisation.wording?.trim()) {
      throw new ValidationError("Enter the exact wording for your sign.");
    }
    if (personalisation.wording.length > 256) {
      throw new ValidationError("Sign wording must be 256 characters or fewer.");
    }
  }

  if (!personalisation.wordingConfirmed) {
    throw new ValidationError("Confirm that you have checked the wording and selected options.");
  }

  if (personalisation.note && personalisation.note.length > 2000) {
    throw new ValidationError("The additional note must be 2,000 characters or fewer.");
  }
}

export function validateCartLine(product: Product, line: CartLine): ProductVariant {
  const variant = product.variants.find((candidate) => candidate.id === line.variantId);
  if (!variant || !variant.available) {
    throw new ValidationError(`${product.name} is not available in that configuration.`, 409);
  }
  if (!Number.isInteger(line.quantity) || line.quantity < 1 || line.quantity > 10) {
    throw new ValidationError("Quantity must be between 1 and 10.");
  }
  validatePersonalisation(product, line.personalisation);
  return variant;
}

export function lineSummary(product: Product, variant: ProductVariant, personalisation: Personalisation): string {
  const wording = product.personalisationMode === "pair"
    ? `${personalisation.wordingOne} / ${personalisation.wordingTwo}`
    : personalisation.wording;
  const styleLabel = product.styleSelector === "design" ? "Design" : "Lettering";
  return `${variant.label}; ${styleLabel} ${personalisation.style}; ${wording}`.slice(0, 500);
}
